import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Footer from './components/Footer';
import { supabase } from './supabase';
import { getGoogleDriveDirectLink } from './utils/uploadUtils';
import { Teacher, OnlineClass } from './types';
import OnlineClassCard from './components/OnlineClassCard';
import { sendMessage } from './services/nvidiaService';
import useSeoMeta from './hooks/useSeoMeta';

/* ─── Gradient fallbacks for teacher avatars ─── */
const GRADIENTS = [
  'linear-gradient(135deg,#4647d3 0%,#0050d4 100%)',
  'linear-gradient(135deg,#8126cf 0%,#4647d3 100%)',
  'linear-gradient(135deg,#0050d4 0%,#00c6fb 100%)',
  'linear-gradient(135deg,#f093fb 0%,#f5576c 100%)',
  'linear-gradient(135deg,#43e97b 0%,#38f9d7 100%)',
  'linear-gradient(135deg,#fa709a 0%,#fee140 100%)',
];
const gradientFor = (name: string) =>
  GRADIENTS[name.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % GRADIENTS.length];

const HOME_NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Find Teachers', to: '/find-teachers' },
  { label: 'Contact', to: '/contact' },
] as const;

const HOME_CANONICAL_PATH = '/';
const HOME_LOGO_PATH = '/ln.jpg';
const HOME_LOGO_URL = 'https://www.lankatutor.me/ln.jpg';

/* ─── Inline styles ─── */
const styles: Record<string, React.CSSProperties> = {
  /* ── root ── */
  root: {
    fontFamily: "'Plus Jakarta Sans', 'Lexend', system-ui, sans-serif",
    background: '#f5f7f9',
    color: '#2c2f31',
    overflowX: 'hidden',
  },
  /* ── nav ── */
  nav: {
    position: 'fixed',
    top: 0, left: 0, right: 0,
    height: 72,
    background: 'rgba(255,255,255,0.72)',
    backdropFilter: 'blur(20px)',
    borderBottom: '1px solid rgba(255,255,255,0.2)',
    boxShadow: '0 4px 20px rgba(70,71,211,0.04)',
    zIndex: 100,
  },
  navInner: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 2.5rem',
    gap: '1rem',
  },
  navBrandGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '3rem',
    minWidth: 0,
  },
  navLogoLink: {
    display: 'inline-flex',
    alignItems: 'center',
    textDecoration: 'none',
    flexShrink: 0,
  },
  navLogo: {
    fontSize: '1.4rem',
    fontWeight: 900,
    letterSpacing: '-0.04em',
    background: 'linear-gradient(135deg,#4647d3,#0050d4)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  navLinks: {
    display: 'flex',
    gap: '2rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#595c5e',
    listStyle: 'none',
    margin: 0, padding: 0,
  },
  navActions: {
    display: 'flex',
    gap: '0.6rem',
    alignItems: 'center',
    flexShrink: 0,
  },
  navMenuButton: {
    width: 46,
    height: 46,
    borderRadius: 16,
    border: '1px solid rgba(70,71,211,0.12)',
    background: 'rgba(255,255,255,0.88)',
    boxShadow: '0 10px 26px rgba(70,71,211,0.12)',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    cursor: 'pointer',
    padding: 0,
    flexShrink: 0,
  },
  navMenuLine: {
    width: 18,
    height: 2,
    borderRadius: 999,
    background: '#2c2f31',
    transition: 'transform 0.2s ease, opacity 0.2s ease',
  },
  navMobileMenu: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(15,23,42,0.28)',
    backdropFilter: 'blur(6px)',
    zIndex: 120,
  },
  navMobileSheet: {
    position: 'absolute' as const,
    left: '0.75rem',
    right: '0.75rem',
    bottom: '0.75rem',
    borderRadius: 30,
    background: 'rgba(255,255,255,0.97)',
    border: '1px solid rgba(70,71,211,0.12)',
    boxShadow: '0 30px 80px rgba(15, 23, 42, 0.18)',
    backdropFilter: 'blur(24px)',
    padding: '1rem',
    transition: 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.25s ease',
  },
  navMobileGrabber: {
    width: 56,
    height: 6,
    borderRadius: 999,
    background: 'rgba(148,163,184,0.55)',
    margin: '0 auto 1rem',
  },
  navMobileHeader: {
    marginBottom: '1rem',
  },
  navMobileEyebrow: {
    margin: 0,
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: '0.18em',
    textTransform: 'uppercase' as const,
    color: '#4f46e5',
  },
  navMobileTitle: {
    margin: '0.35rem 0 0',
    fontSize: '0.95rem',
    color: '#64748b',
    lineHeight: 1.5,
  },
  navMobileLinks: {
    display: 'grid',
    gap: '0.5rem',
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  navMobileLink: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.95rem 1rem',
    borderRadius: 18,
    textDecoration: 'none',
    color: '#334155',
    fontSize: '0.98rem',
    fontWeight: 700,
    background: 'rgba(241,245,249,0.8)',
    transition: 'transform 0.2s ease, background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
  },
  navMobileActions: {
    display: 'grid',
    gap: '0.75rem',
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: '1px solid rgba(148,163,184,0.18)',
  },
  navMobileButtonLink: {
    textDecoration: 'none',
    display: 'block',
  },
  teacherLoginBtn: {
    background: 'linear-gradient(135deg,#4647d3,#0050d4)',
    color: '#fff',
    border: 'none',
    padding: '0.6rem 1.2rem',
    borderRadius: 12,
    fontWeight: 700,
    fontSize: '0.82rem',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(70,71,211,0.25)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  studentLoginBtn: {
    background: 'linear-gradient(135deg,#10b981,#059669)',
    color: '#fff',
    border: 'none',
    padding: '0.6rem 1.2rem',
    borderRadius: 12,
    fontWeight: 700,
    fontSize: '0.82rem',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(16,185,129,0.24)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  /* ── teacher slide strip ── */
  slideStrip: {
    width: '100%',
    overflow: 'hidden',
    background: 'transparent',
    paddingTop: 72,
    position: 'relative',
  },
  slideTrack: {
    display: 'flex',
    gap: 12,
    padding: '12px 0',
    transition: 'transform 0.6s cubic-bezier(0.4,0,0.2,1)',
    willChange: 'transform',
  },
  slideCard: {
    flexShrink: 0,
    width: 80,
    height: 80,
    borderRadius: '50%',
    overflow: 'hidden',
    border: '3px solid #fff',
    boxShadow: '0 4px 16px rgba(70,71,211,0.18)',
    position: 'relative',
    transition: 'transform 0.3s, box-shadow 0.3s',
    cursor: 'pointer',
  },
  /* ── hero ── */
  hero: {
    minHeight: '90vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column' as const,
    textAlign: 'center' as const,
    padding: '4rem 1.5rem 6rem',
    position: 'relative' as const,
    overflow: 'hidden',
  },
  blob1: {
    position: 'absolute',
    width: 500, height: 500,
    borderRadius: '50%',
    top: -80, left: -80,
    background: 'rgba(70,71,211,0.08)',
    filter: 'blur(60px)',
    zIndex: 0,
    animation: 'floatBlob 6s ease-in-out infinite',
  },
  blob2: {
    position: 'absolute',
    width: 400, height: 400,
    borderRadius: '50%',
    bottom: 60, right: -80,
    background: 'rgba(129,38,207,0.08)',
    filter: 'blur(60px)',
    zIndex: 0,
    animationDelay: '2s',
    animation: 'floatBlob 6s ease-in-out infinite',
  },
  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.4rem 1rem',
    borderRadius: 9999,
    background: 'rgba(255,255,255,0.5)',
    border: '1px solid rgba(71,71,211,0.15)',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase' as const,
    color: '#4647d3',
    marginBottom: '1.5rem',
    zIndex: 1,
  },
  heroTitle: {
    fontSize: 'clamp(2.6rem,7vw,5.5rem)',
    fontWeight: 900,
    letterSpacing: '-0.04em',
    lineHeight: 1.08,
    color: '#2c2f31',
    marginBottom: '1.25rem',
    zIndex: 1,
  },
  heroGrad: {
    background: 'linear-gradient(90deg,#4647d3,#0050d4,#8126cf)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  heroSub: {
    fontSize: '1.1rem',
    color: '#595c5e',
    maxWidth: 560,
    lineHeight: 1.7,
    marginBottom: '3rem',
    zIndex: 1,
  },
  searchBar: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    gap: 12,
    background: 'rgba(255,255,255,0.75)',
    backdropFilter: 'blur(24px)',
    border: '1.5px solid rgba(171,173,175,0.25)',
    borderRadius: 24,
    padding: '0.75rem',
    boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
    maxWidth: 760,
    width: '100%',
    zIndex: 1,
  },
  searchInput: {
    flex: 1,
    minWidth: 180,
    border: 'none',
    background: 'transparent',
    fontSize: '0.9rem',
    fontWeight: 500,
    fontFamily: 'inherit',
    color: '#2c2f31',
    outline: 'none',
    padding: '0.5rem 0.75rem',
  },
  searchSelect: {
    border: 'none',
    background: 'transparent',
    fontSize: '0.875rem',
    fontWeight: 500,
    fontFamily: 'inherit',
    color: '#2c2f31',
    outline: 'none',
    cursor: 'pointer',
    padding: '0.5rem 0.5rem',
  },
  searchBtn: {
    background: 'linear-gradient(135deg,#4647d3,#0050d4)',
    color: '#fff',
    border: 'none',
    padding: '0.9rem 2rem',
    borderRadius: 16,
    fontWeight: 700,
    fontSize: '0.9rem',
    fontFamily: 'inherit',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    boxShadow: '0 4px 18px rgba(70,71,211,0.3)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    whiteSpace: 'nowrap' as const,
  },
  divider: {
    width: 1,
    height: 32,
    background: 'rgba(171,173,175,0.3)',
    flexShrink: 0,
  },
  /* ── stats ── */
  stats: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    justifyContent: 'center',
    gap: '1.5rem',
    marginTop: '3rem',
    zIndex: 1,
  },
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    background: 'rgba(255,255,255,0.7)',
    backdropFilter: 'blur(24px)',
    border: '1.5px solid rgba(171,173,175,0.2)',
    padding: '0.75rem 1.25rem',
    borderRadius: 18,
    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
    animation: 'floatBlob 6s ease-in-out infinite',
  },
  statIcon: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statNum: { fontSize: '1.25rem', fontWeight: 800, color: '#2c2f31', lineHeight: 1 },
  statLabel: { fontSize: '0.65rem', fontWeight: 700, color: '#595c5e', textTransform: 'uppercase' as const, letterSpacing: '0.08em' },
  /* ── section ── */
  section: { padding: '5rem 1.5rem', maxWidth: 1280, margin: '0 auto' },
  sectionAlt: { background: '#eef1f3', padding: '5rem 1.5rem' },
  sectionInner: { maxWidth: 1280, margin: '0 auto' },
  sectionHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '3rem', flexWrap: 'wrap' as const, gap: '1rem' },
  sectionTitle: { fontSize: 'clamp(1.8rem,3vw,2.5rem)', fontWeight: 900, color: '#2c2f31', marginBottom: '0.5rem' },
  sectionSub: { fontSize: '1rem', color: '#595c5e', maxWidth: 420 },
  viewAll: {
    display: 'flex', alignItems: 'center', gap: '0.3rem',
    color: '#4647d3', fontWeight: 700, fontSize: '0.9rem',
    textDecoration: 'none', transition: 'gap 0.2s',
    border: 'none', background: 'transparent', cursor: 'pointer',
  },
  /* ── teacher grid ── */
  teacherGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: '1.5rem' },
  teacherCard: {
    background: '#fff',
    borderRadius: 24,
    overflow: 'hidden',
    transition: 'transform 0.4s, box-shadow 0.4s',
    textDecoration: 'none',
    display: 'block',
    color: 'inherit',
  },
  teacherImgWrap: { aspectRatio: '1', overflow: 'hidden', position: 'relative' as const },
  teacherImg: { width: '100%', height: '100%', objectFit: 'cover' as const, transition: 'transform 0.5s' },
  teacherBody: { padding: '1.25rem', display: 'flex', flexDirection: 'column' as const, gap: '0.5rem' },
  subjectPill: {
    display: 'inline-flex', alignItems: 'center',
    padding: '0.25rem 0.75rem',
    borderRadius: 8,
    background: 'rgba(70,71,211,0.08)',
    color: '#4647d3',
    fontSize: '0.65rem',
    fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em',
  },
  teacherName: { fontSize: '1.1rem', fontWeight: 800, color: '#2c2f31' },
  teacherMeta: { fontSize: '0.8rem', color: '#595c5e' },
  locPill: {
    padding: '0.2rem 0.6rem', borderRadius: 8,
    background: '#eef1f3',
    color: '#595c5e', fontSize: '0.65rem', fontWeight: 700,
    textTransform: 'uppercase' as const, letterSpacing: '0.06em',
  },
  /* ── AI section ── */
  aiSection: {
    background: 'linear-gradient(135deg,#270048 0%,#4647d3 100%)',
    borderRadius: 48,
    padding: 'clamp(2.5rem,5vw,5rem)',
    margin: '0 1.5rem 5rem',
    maxWidth: 1280,
    marginLeft: 'auto', marginRight: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '4rem',
    flexWrap: 'wrap' as const,
    position: 'relative' as const,
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(129,38,207,0.28)',
  },
  aiGlow: {
    position: 'absolute',
    width: 800, height: 800,
    borderRadius: '50%',
    background: 'rgba(129,38,207,0.15)',
    filter: 'blur(120px)',
    top: '50%', left: '50%',
    transform: 'translate(-50%,-50%)',
    zIndex: 0,
  },
  aiBadge: {
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    padding: '0.4rem 1rem', borderRadius: 9999,
    background: 'rgba(206,155,255,0.2)',
    color: '#ce9bff',
    fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const,
    marginBottom: '1rem',
  },
  aiTitle: { fontSize: 'clamp(1.8rem,3vw,2.8rem)', fontWeight: 900, color: '#fff', lineHeight: 1.2, marginBottom: '1rem' },
  aiDesc: { fontSize: '1rem', color: 'rgba(206,155,255,0.8)', lineHeight: 1.7, marginBottom: '1.75rem' },
  aiFeatGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '0.75rem', marginBottom: '2rem' },
  aiFeat: {
    display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
    padding: '1rem', borderRadius: 16,
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  aiFeatTitle: { color: '#fff', fontWeight: 700, fontSize: '0.9rem' },
  aiFeatSub: { color: 'rgba(206,155,255,0.6)', fontSize: '0.78rem', marginTop: 2 },
  aiCta: {
    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
    background: '#fff',
    color: '#4647d3',
    border: 'none', cursor: 'pointer',
    padding: '0.9rem 2rem', borderRadius: 14,
    fontWeight: 800, fontSize: '0.95rem', fontFamily: 'inherit',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  chatBox: {
    flex: '0 0 340px',
    background: 'rgba(255,255,255,0.08)',
    backdropFilter: 'blur(24px)',
    border: '1.5px solid rgba(255,255,255,0.15)',
    borderRadius: 28,
    padding: '1.5rem',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    zIndex: 1,
    maxWidth: '100%',
  },
  chatHeader: { display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' },
  chatAvatar: {
    width: 40, height: 40, borderRadius: '50%',
    background: 'linear-gradient(135deg,#8126cf,#ce9bff)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
  },
  chatOnline: { width: 8, height: 8, borderRadius: '50%', background: '#4ade80', display: 'inline-block', marginRight: 4 },
  chatMsgUser: {
    marginLeft: '2rem', padding: '0.75rem 1rem',
    background: 'rgba(255,255,255,0.1)', borderRadius: '16px 16px 4px 16px',
    color: '#fff', fontSize: '0.85rem', marginBottom: '0.75rem',
  },
  chatMsgBot: {
    marginRight: '2rem', padding: '0.75rem 1rem',
    background: 'rgba(129,38,207,0.35)', borderRadius: '16px 16px 16px 4px',
    color: '#fff', fontSize: '0.85rem', marginBottom: '0.75rem',
  },
  typingDot: {
    display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
    background: '#ce9bff', margin: '0 2px',
    animation: 'bounce 1.2s ease-in-out infinite',
  },
  /* ── CTA ── */
  ctaSec: { padding: '3rem 1.5rem 5rem' },
  ctaCard: {
    background: '#0b0f10',
    borderRadius: 56,
    padding: 'clamp(3rem,6vw,6rem)',
    textAlign: 'center' as const,
    position: 'relative' as const,
    overflow: 'hidden',
    maxWidth: 1280,
    margin: '0 auto',
  },
  ctaTitle: { fontSize: 'clamp(2.2rem,5vw,4.5rem)', fontWeight: 900, color: '#fff', letterSpacing: '-0.04em', lineHeight: 1, marginBottom: '1rem', position: 'relative' as const, zIndex: 1 },
  ctaSub: { fontSize: '1.1rem', color: '#9a9d9f', marginBottom: '2.5rem', position: 'relative' as const, zIndex: 1 },
  ctaBtnPrimary: {
    background: 'linear-gradient(135deg,#4647d3,#0050d4)',
    color: '#fff', border: 'none', cursor: 'pointer',
    padding: '1rem 2.5rem', borderRadius: 16,
    fontWeight: 800, fontSize: '1.05rem', fontFamily: 'inherit',
    boxShadow: '0 4px 24px rgba(70,71,211,0.4)',
    transition: 'transform 0.2s',
    textDecoration: 'none',
    display: 'inline-flex',
  },
  ctaBtnSecondary: {
    background: 'rgba(255,255,255,0.08)',
    backdropFilter: 'blur(10px)',
    color: '#fff',
    border: '1.5px solid rgba(255,255,255,0.15)',
    cursor: 'pointer',
    padding: '1rem 2.5rem', borderRadius: 16,
    fontWeight: 800, fontSize: '1.05rem', fontFamily: 'inherit',
    transition: 'background 0.2s',
    textDecoration: 'none',
    display: 'inline-flex',
  },
};

/* ─── Keyframes injected once ─── */
const KEYFRAMES = `
@keyframes floatBlob { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-18px)} }
@keyframes bounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-8px)} }
@keyframes slideLeft { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
`;

/* ══════════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════════ */
export default function HomePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCompactNav, setIsCompactNav] = useState(() => window.innerWidth < 960);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [teacherCount, setTeacherCount] = useState(0);
  const [districtCount, setDistrictCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [topTeachers, setTopTeachers] = useState<Teacher[]>([]);
  const [allTeachersForSlide, setAllTeachersForSlide] = useState<Teacher[]>([]);
  const [onlineClasses, setOnlineClasses] = useState<OnlineClass[]>([]);
  const [aiSearchQuery, setAiSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [imgErrors, setImgErrors] = useState<Record<string, boolean>>({});
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [carouselAnimating, setCarouselAnimating] = useState(false);
  const [carouselDir, setCarouselDir] = useState<'left'|'right'>('left');
  const carouselTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const seoStructuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: 'lankatutor',
        url: 'https://www.lankatutor.me/',
        logo: HOME_LOGO_URL,
        image: HOME_LOGO_URL,
      },
      {
        '@type': 'WebSite',
        name: 'lankatutor',
        url: 'https://www.lankatutor.me/',
      },
    ],
  };

  useSeoMeta({
    title: 'lankatutor - Find the Best Teachers in Sri Lanka',
    description: 'lankatutor helps students across Sri Lanka discover trusted teachers for O/L, A/L, and online classes.',
    canonicalPath: HOME_CANONICAL_PATH,
    image: HOME_LOGO_PATH,
    keywords: ['lankatutor', 'Sri Lanka teachers', 'online classes', 'tuition teachers'],
    robots: 'index,follow',
    openGraphType: 'website',
    structuredData: seoStructuredData,
  });

  /* ── Redirect if logged in ── */
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        const role = localStorage.getItem('userRole');
        if (role === 'student') navigate('/student-dashboard', { replace: true });
        else if (role === 'teacher') navigate('/dashboard', { replace: true });
      }
    });
  }, [navigate]);

  useEffect(() => {
    const handleResize = () => {
      const compact = window.innerWidth < 960;
      setIsCompactNav(compact);
      if (!compact) {
        setIsMobileMenuOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const homeNavGroups = useMemo(
    () => ({
      '/': new Set(['/']),
      '/about': new Set(['/about']),
      '/find-teachers': new Set(['/find-teachers']),
      '/contact': new Set(['/contact']),
    }),
    []
  );

  const isHomeNavActive = (path: string) => {
    if (path === '/find-teachers' && location.pathname.startsWith('/teacher/')) {
      return true;
    }

    return homeNavGroups[path as keyof typeof homeNavGroups]?.has(location.pathname) ?? false;
  };

  /* ── Fetch data ── */
  useEffect(() => {
    (async () => {
      try {
        const { data: teachersData } = await supabase.from('teachers').select('*');
        const teachers = teachersData || [];
        setTeacherCount(teachers.length);
        const uniqueDistricts = new Set(teachers.map((t: any) => t.district).filter(Boolean));
        setDistrictCount(uniqueDistricts.size);
        const mapped: Teacher[] = teachers.map((t: any) => ({ ...t, imageUrl: t.image_url }));
        setTopTeachers(mapped.slice(0, 4));
        setAllTeachersForSlide(mapped);
      } catch { /* silent */ } finally { setLoading(false); }
    })();
    (async () => {
      try {
        const { data: classesData } = await supabase.from('online_classes').select('*');
        const now = new Date();
        const upcoming = (classesData || [])
          .filter((cls: any) => new Date(cls.date) >= now)
          .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
          .slice(0, 6);
        setOnlineClasses(upcoming);
      } catch { /* silent */ }
    })();
  }, []);

  /* ── Carousel auto-advance ── */
  const advanceCarousel = useCallback((dir: 'left'|'right' = 'left') => {
    if (!allTeachersForSlide.length || carouselAnimating) return;
    setCarouselDir(dir);
    setCarouselAnimating(true);
    setTimeout(() => {
      setCarouselIdx(prev =>
        dir === 'left'
          ? (prev + 1) % allTeachersForSlide.length
          : (prev - 1 + allTeachersForSlide.length) % allTeachersForSlide.length
      );
      setCarouselAnimating(false);
    }, 420);
  }, [allTeachersForSlide.length, carouselAnimating]);

  useEffect(() => {
    if (!allTeachersForSlide.length) return;
    carouselTimer.current = setInterval(() => advanceCarousel('left'), 3000);
    return () => clearInterval(carouselTimer.current);
  }, [allTeachersForSlide.length, advanceCarousel]);

  const handleSmartSearch = async () => {
    if (!aiSearchQuery.trim()) {
      navigate('/find-teachers');
      return;
    }
    
    setIsSearching(true);
    try {
        const teacherDataMin = allTeachersForSlide.map(t => ({
            id: t.id,
            subject: t.subject,
            district: t.district,
            bio: t.bio || ''
        }));
        
        const systemPrompt = `You are a matchmaking AI for a tutoring platform. 
Here is a JSON list of available teachers:
${JSON.stringify(teacherDataMin)}

The student's request is: "${aiSearchQuery}"

Return ONLY a JSON array containing the string IDs of the top 3 best matching teachers based on the request. Example: ["id1", "id2"]. Try to match the subject primarily, followed by location and bio details. Do NOT output any explanation, markdown, or text other than the raw JSON array.`;

        const response = await sendMessage([], aiSearchQuery, systemPrompt);
        
        const cleanResponse = response.replace(/```json/gi, '').replace(/```/g, '').trim();
        const ids = JSON.parse(cleanResponse);
        
        if (Array.isArray(ids) && ids.length > 0) {
            navigate(`/find-teachers?aiIds=${ids.join(',')}`);
        } else {
            navigate(`/find-teachers`);
        }
    } catch (err) {
        console.error("AI Search Failed", err);
        navigate(`/find-teachers`);
    } finally {
        setIsSearching(false);
    }
  };

  const mobileMenuLineTopStyle: React.CSSProperties = isMobileMenuOpen
    ? { ...styles.navMenuLine, transform: 'translateY(6px) rotate(45deg)' }
    : styles.navMenuLine;

  const mobileMenuLineMiddleStyle: React.CSSProperties = isMobileMenuOpen
    ? { ...styles.navMenuLine, opacity: 0 }
    : styles.navMenuLine;

  const mobileMenuLineBottomStyle: React.CSSProperties = isMobileMenuOpen
    ? { ...styles.navMenuLine, transform: 'translateY(-6px) rotate(-45deg)' }
    : styles.navMenuLine;

  return (
    <>
      <style>{KEYFRAMES}</style>

      <div style={styles.root}>
        {/* ═══ NAVBAR ═══ */}
        <nav style={styles.nav}>
          <div
            style={{
              ...styles.navInner,
              padding: isCompactNav ? '0 1rem' : styles.navInner.padding,
            }}
          >
            <div style={{ ...styles.navBrandGroup, gap: isCompactNav ? '1rem' : styles.navBrandGroup.gap }}>
              <Link to="/" style={styles.navLogoLink}>
                <span style={styles.navLogo}>LankaTutor</span>
              </Link>
              {!isCompactNav && (
                <ul style={styles.navLinks}>
                  {HOME_NAV_LINKS.map(({ label, to }) => (
                    <li key={label}>
                      <Link
                        to={to}
                        style={{
                          color: isHomeNavActive(to) ? '#4647d3' : '#595c5e',
                          textDecoration: 'none',
                          fontSize: '0.875rem',
                          fontWeight: isHomeNavActive(to) ? 700 : 500,
                          transition: 'color 0.2s',
                          position: 'relative',
                          paddingBottom: '0.3rem',
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.color = '#4647d3';
                        }}
                        onMouseLeave={e => {
                          if (!isHomeNavActive(to)) {
                            e.currentTarget.style.color = '#595c5e';
                          }
                        }}
                      >
                        {label}
                        {isHomeNavActive(to) && (
                          <span
                            style={{
                              position: 'absolute',
                              left: '50%',
                              bottom: '-0.2rem',
                              transform: 'translateX(-50%)',
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background: '#4647d3',
                            }}
                          />
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {isCompactNav ? (
              <button
                type="button"
                style={styles.navMenuButton}
                onClick={() => setIsMobileMenuOpen(prev => !prev)}
                aria-label="Toggle home page menu"
                aria-expanded={isMobileMenuOpen}
              >
                <span style={mobileMenuLineTopStyle} />
                <span style={mobileMenuLineMiddleStyle} />
                <span style={mobileMenuLineBottomStyle} />
              </button>
            ) : (
              <div style={styles.navActions}>
                <Link to="/login">
                  <button
                    style={styles.teacherLoginBtn}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.04)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
                  >Teacher Login</button>
                </Link>
                <Link to="/student-login">
                  <button
                    style={styles.studentLoginBtn}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.04)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
                  >Student Login</button>
                </Link>
              </div>
            )}
          </div>

        </nav>

        {isCompactNav && (
          <div
            style={{
              ...styles.navMobileMenu,
              opacity: isMobileMenuOpen ? 1 : 0,
              pointerEvents: isMobileMenuOpen ? 'auto' : 'none',
              transition: 'opacity 0.25s ease',
            }}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <div
              style={{
                ...styles.navMobileSheet,
                transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(calc(100% + 2rem))',
                opacity: isMobileMenuOpen ? 1 : 0,
              }}
              onClick={e => e.stopPropagation()}
            >
              <div style={styles.navMobileGrabber} />
              <div style={styles.navMobileHeader}>
                <p style={styles.navMobileEyebrow}>Explore LankaTutor</p>
                <p style={styles.navMobileTitle}>Browse the main pages and jump into the right login flow from one mobile-friendly menu.</p>
              </div>

              <ul style={styles.navMobileLinks}>
                {HOME_NAV_LINKS.map(({ label, to }) => (
                  <li key={label}>
                    <Link
                      to={to}
                      style={{
                        ...styles.navMobileLink,
                        background: isHomeNavActive(to) ? 'linear-gradient(135deg,rgba(70,71,211,0.12),rgba(0,80,212,0.08))' : styles.navMobileLink.background,
                        color: isHomeNavActive(to) ? '#312e81' : styles.navMobileLink.color,
                        boxShadow: isHomeNavActive(to) ? '0 10px 24px rgba(70,71,211,0.08)' : 'none',
                      }}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span>{label}</span>
                      <span className="material-symbols-outlined" style={{ fontSize: 18, color: 'inherit' }}>arrow_forward</span>
                    </Link>
                  </li>
                ))}
              </ul>

              <div style={styles.navMobileActions}>
                <Link to="/login" style={styles.navMobileButtonLink} onClick={() => setIsMobileMenuOpen(false)}>
                  <button style={{ ...styles.teacherLoginBtn, width: '100%', padding: '0.95rem 1.2rem', fontSize: '0.92rem' }}>Teacher Login</button>
                </Link>
                <Link to="/student-login" style={styles.navMobileButtonLink} onClick={() => setIsMobileMenuOpen(false)}>
                  <button style={{ ...styles.studentLoginBtn, width: '100%', padding: '0.95rem 1.2rem', fontSize: '0.92rem' }}>Student Login</button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* ═══ TEACHER PHOTO CAROUSEL ═══ */}
        {allTeachersForSlide.length > 0 && (() => {
          const t = allTeachersForSlide[carouselIdx];
          const hasImg = t.imageUrl && !imgErrors[`slide-${t.id}`];
          const slideAnim: React.CSSProperties = carouselAnimating
            ? { transform: carouselDir === 'left' ? 'translateX(-40px)' : 'translateX(40px)', opacity: 0 }
            : { transform: 'translateX(0)', opacity: 1 };
          return (
            <div style={{ paddingTop: 72, background: 'linear-gradient(135deg,rgba(70,71,211,0.04),rgba(129,38,207,0.04))', borderBottom: '1px solid rgba(70,71,211,0.08)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 24px 14px', gap: 12 }}>

                {/* Photo only */}
                <Link
                  to={`/teacher/${t.id}`}
                  style={{ textDecoration: 'none', display: 'block', transition: 'transform 0.42s cubic-bezier(0.4,0,0.2,1), opacity 0.42s', ...slideAnim }}
                >
                  <div style={{ width: 130, height: 130, borderRadius: 22, overflow: 'hidden', border: '3px solid #fff', boxShadow: '0 8px 32px rgba(70,71,211,0.2)', background: gradientFor(t.name) }}>
                    {hasImg ? (
                      <img src={getGoogleDriveDirectLink(t.imageUrl!)} alt={t.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={() => setImgErrors(prev => ({ ...prev, [`slide-${t.id}`]: true }))} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', fontWeight: 900, color: '#fff' }}>{t.name.charAt(0)}</div>
                    )}
                  </div>
                </Link>

                {/* Dots */}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {allTeachersForSlide.slice(0, Math.min(allTeachersForSlide.length, 10)).map((_, i) => (
                    <div
                      key={i}
                      onClick={() => { clearInterval(carouselTimer.current); setCarouselIdx(i); }}
                      style={{ width: carouselIdx === i ? 22 : 6, height: 6, borderRadius: 9999, background: carouselIdx === i ? '#4647d3' : 'rgba(70,71,211,0.2)', transition: 'all 0.3s', cursor: 'pointer' }}
                    />
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ═══ HERO ═══ */}
        <section style={styles.hero}>
          <div style={styles.blob1} />
          <div style={styles.blob2} />

          <div style={styles.heroBadge}>
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>school</span>
            Sri Lanka's Premier Education Platform
          </div>

          <h1 style={styles.heroTitle}>
            Find the Best Teachers in<br />
            <span style={styles.heroGrad}>Sri Lanka</span>
          </h1>
          <p style={styles.heroSub}>
            Connect with 1,200+ verified expert tutors for O/L and A/L examinations.
            Master your curriculum with personalized sessions.
          </p>

          {/* Search Bar */}
          <div style={styles.searchBar}>
            <span className="material-symbols-outlined" style={{ color: '#ce9bff', fontSize: 24, flexShrink: 0, marginLeft: '0.5rem' }}>auto_awesome</span>
            <input
              id="home-smart-search"
              name="smartSearch"
              style={{ ...styles.searchInput, fontSize: '0.95rem' }}
              placeholder="E.g. I need a Physics teacher in Colombo who explains mechanics well..."
              value={aiSearchQuery}
              onChange={e => setAiSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !isSearching && handleSmartSearch()}
              disabled={isSearching}
            />
            
            <button
              style={{...styles.searchBtn, background: 'linear-gradient(135deg, #197fe6, #8b5cf6)'}}
              onClick={handleSmartSearch}
              disabled={isSearching}
              onMouseEnter={e => !isSearching && (e.currentTarget.style.transform = 'scale(1.03)')}
              onMouseLeave={e => !isSearching && (e.currentTarget.style.transform = 'scale(1)')}>
              {isSearching ? (
                 <div style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              ) : (
                <>
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>search</span>
                  AI Smart Search
                </>
              )}
            </button>
          </div>

          {/* Stats */}
          <div style={styles.stats}>
            <div style={{ ...styles.statCard, animationDelay: '0s' }}>
              <div style={{ ...styles.statIcon, background: '#dcfce7', color: '#16a34a' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 22 }}>verified</span>
              </div>
              <div>
                <div style={styles.statNum}>{loading ? '—' : `${teacherCount > 0 ? teacherCount : '1,200'}+`}</div>
                <div style={styles.statLabel}>Verified Teachers</div>
              </div>
            </div>
            <div style={{ ...styles.statCard, animationDelay: '1.5s' }}>
              <div style={{ ...styles.statIcon, background: '#dbeafe', color: '#2563eb' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 22 }}>map</span>
              </div>
              <div>
                <div style={styles.statNum}>{loading ? '—' : `${districtCount > 0 ? districtCount : '25'}+`}</div>
                <div style={styles.statLabel}>Districts Covered</div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══ AI ASSISTANT ═══ */}
        <div style={{ padding: '0 1.5rem 5rem', maxWidth: 1280, marginLeft: 'auto', marginRight: 'auto' }}>
          <div style={styles.aiSection}>
            <div style={styles.aiGlow} />

            {/* Left */}
            <div style={{ flex: 1, minWidth: 280, position: 'relative', zIndex: 1 }}>
              <div style={styles.aiBadge}>
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>auto_awesome</span>
                Intelligent Learning
              </div>
              <h2 style={styles.aiTitle}>Meet Your AI Study Assistant</h2>
              <p style={styles.aiDesc}>
                Experience the future of Sri Lankan education. Our AI tutor understands local
                syllabuses and provides instant, 24/7 academic support.
              </p>
              <div style={styles.aiFeatGrid}>
                {[
                  { icon: 'psychology', title: 'Smart Tutoring', sub: 'Step-by-step guidance' },
                  { icon: 'upload_file', title: 'Upload PDFs', sub: 'Analyze your notes' },
                  { icon: 'translate', title: 'Multi-Language', sub: 'Sinhala, Tamil, English' },
                  { icon: 'quiz', title: 'Quiz Generator', sub: 'Instant practice tests' },
                ].map(f => (
                  <div key={f.title} style={styles.aiFeat}>
                    <span className="material-symbols-outlined" style={{ color: '#ce9bff', fontSize: 22, flexShrink: 0 }}>{f.icon}</span>
                    <div>
                      <div style={styles.aiFeatTitle}>{f.title}</div>
                      <div style={styles.aiFeatSub}>{f.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
              <button
                style={styles.aiCta}
                onClick={() => navigate('/student-login')}
                onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.04)')}
                onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}>
                Student Login
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_forward</span>
              </button>
            </div>

            {/* Right – chat mockup */}
            <div style={{ display: 'flex', justifyContent: 'center', flex: '0 1 360px', zIndex: 1 }}>
              <div style={styles.chatBox}>
                <div style={styles.chatHeader}>
                  <div style={styles.chatAvatar}>
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>bolt</span>
                  </div>
                  <div>
                    <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.875rem' }}>LankaTutor AI</div>
                    <div style={{ fontSize: '0.7rem', color: '#ce9bff', fontWeight: 600, marginTop: 2 }}>
                      <span style={styles.chatOnline} />Online Now
                    </div>
                  </div>
                </div>
                <div style={styles.chatMsgUser}>Can you explain quadratic equations using a simple example?</div>
                <div style={styles.chatMsgBot}>
                  <div style={{ color: '#ce9bff', fontWeight: 700, marginBottom: 4 }}>Sure!</div>
                  Think of it like a curve. A quadratic equation looks like <em>ax² + bx + c = 0</em>. Let's solve <em>x² - 5x + 6 = 0</em>…
                </div>
                <div style={{ color: 'rgba(206,155,255,0.5)', fontSize: '0.75rem', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ ...styles.typingDot, animationDelay: '0s' }} />
                  <span style={{ ...styles.typingDot, animationDelay: '0.2s' }} />
                  <span style={{ ...styles.typingDot, animationDelay: '0.4s' }} />
                  AI is typing a solution…
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ═══ RECENT TEACHERS ═══ */}
        <div style={styles.section}>
          <div style={styles.sectionHead}>
            <div>
              <h2 style={styles.sectionTitle}>Recent Teachers</h2>
              <p style={styles.sectionSub}>Highly rated professionals who recently joined our elite network.</p>
            </div>
            <Link to="/find-teachers" style={styles.viewAll}
              onMouseEnter={e => (e.currentTarget.style.gap = '0.6rem')}
              onMouseLeave={e => (e.currentTarget.style.gap = '0.3rem')}>
              View All Tutors <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_forward</span>
            </Link>
          </div>

          <div style={styles.teacherGrid}>
            {topTeachers.length > 0 ? topTeachers.map((teacher, i) => {
              const hasImg = teacher.imageUrl && !imgErrors[`card-${teacher.id}`];
              return (
                <Link
                  to={`/teacher/${teacher.id}`}
                  key={teacher.id}
                  style={styles.teacherCard}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-8px)';
                    (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 20px 50px rgba(70,71,211,0.1)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)';
                    (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
                  }}
                >
                  <div style={styles.teacherImgWrap}>
                    {hasImg ? (
                      <img
                        style={styles.teacherImg}
                        src={getGoogleDriveDirectLink(teacher.imageUrl!)}
                        alt={teacher.name}
                        onError={() => setImgErrors(prev => ({ ...prev, [`card-${teacher.id}`]: true }))}
                      />
                    ) : (
                      <div style={{
                        width: '100%', height: '100%', minHeight: 220,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '3.5rem', fontWeight: 900, color: '#fff',
                        background: gradientFor(teacher.name),
                      }}>
                        {teacher.name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div style={styles.teacherBody}>
                    <span style={styles.subjectPill}>{teacher.subject}</span>
                    <h3 style={styles.teacherName}>{teacher.name}</h3>
                    <p style={styles.teacherMeta}>{teacher.bio || `Expert ${teacher.subject} tutor`}</p>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                      {teacher.district && <span style={styles.locPill}>{teacher.district}</span>}
                      {teacher.medium && <span style={styles.locPill}>{teacher.medium} Medium</span>}
                    </div>
                  </div>
                </Link>
              );
            }) : (
              // Skeleton
              [...Array(4)].map((_, i) => (
                <div key={i} style={{ ...styles.teacherCard, background: '#fff' }}>
                  <div style={{ height: 220, background: 'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)', backgroundSize: '200% 100%' }} />
                  <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                    <div style={{ height: 16, width: '40%', borderRadius: 8, background: '#e2e8f0' }} />
                    <div style={{ height: 20, width: '70%', borderRadius: 8, background: '#e2e8f0' }} />
                    <div style={{ height: 14, width: '90%', borderRadius: 8, background: '#e2e8f0' }} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ═══ UPCOMING ONLINE CLASSES ═══ */}
        {onlineClasses.length > 0 && (
          <div style={styles.sectionAlt}>
            <div style={styles.sectionInner}>
              <div style={{ ...styles.section, padding: '2rem 1.5rem' }}>
                <div style={{ ...styles.sectionHead, marginBottom: '2.5rem' }}>
                  <div>
                    <h2 style={styles.sectionTitle}>Upcoming Online Classes</h2>
                    <p style={styles.sectionSub}>Join interactive live sessions from the comfort of your home.</p>
                  </div>
                </div>
                <div style={styles.teacherGrid}>
                  {onlineClasses.map(classItem => (
                    <OnlineClassCard key={classItem.id} classData={classItem} showRegisterButton={true} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══ CTA ═══ */}
        <div style={styles.ctaSec}>
          <div style={styles.ctaCard}>
            <div style={{ position: 'absolute', width: 400, height: 400, borderRadius: '50%', top: -160, left: -160, background: '#4647d3', opacity: 0.35, filter: 'blur(60px)' }} />
            <div style={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', bottom: -80, right: -80, background: '#8126cf', opacity: 0.35, filter: 'blur(60px)' }} />
            <h2 style={styles.ctaTitle}>Ready to start <br />your journey?</h2>
            <p style={styles.ctaSub}>Whether you're looking for the perfect tutor or want to share your knowledge, LankaTutor is here for you.</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
              <Link to="/find-teachers" style={styles.ctaBtnPrimary}>Find a Tutor Today</Link>
              <Link to="/signup" style={styles.ctaBtnSecondary}>Become a Teacher</Link>
            </div>
          </div>
        </div>

        {/* ═══ FOOTER ═══ */}
        <Footer />
      </div>
    </>
  );
}
